// src/theme/Footer/index.tsx
// Custom OverBlock footer component.

import Link from '@docusaurus/Link';
import { useThemeConfig } from '@docusaurus/theme-common';
import NavbarColorModeToggle from '@theme/Navbar/ColorModeToggle';
import ThemedImage from '@theme/ThemedImage';
import Translate from '@docusaurus/Translate';
import React from 'react';
import clsx from 'clsx';
import { FooterLinkItem } from './LinkItem';

interface FooterLinksColumnProps {
  column: { title: string; items: any[] };
}

function FooterLinksColumn({ column }: FooterLinksColumnProps) {
  return (
    <div className="w-full md:w-auto">
      <h3 className="mb-4 text-base font-bold leading-6">{column.title}</h3>
      <ul className="space-y-4">
        {column.items.map((item, i) => (
          <li key={i}>
            <FooterLinkItem item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { footer } = useThemeConfig();
  if (!footer) return null;

  const { links, style } = footer;
  const year = new Date().getFullYear();

  return (
    <footer className={clsx('bg-background text-text', style)}>
      {/* top */}
      <div className="border-t border-separator">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 lg:px-16 py-10">
          <div className="flex flex-col gap-10 md:flex-row">
            {/* left */}
            <div className="flex flex-col gap-8 md:w-1/3">
              <Link to="/" className="w-fit">
                <ThemedImage
                  width="120"
                  alt="OverBlock logo"
                  sources={{ light: '/img/logo-light.svg', dark: '/img/logo-dark.svg' }}
                />
              </Link>
              <NavbarColorModeToggle />
            </div>

            {/* right */}
            <div className="flex flex-col gap-10 md:w-2/3 md:flex-row md:justify-between">
              {links?.slice(0, 3).map((col: any, idx) => <FooterLinksColumn key={idx} column={col} />)}
            </div>
          </div>
        </div>
      </div>

      {/* bottom */}
      <div className="border-t border-separator">
        <div className="max-w-screen-xl mx-auto px-6 md:px-10 lg:px-16 py-6">
          <div className="flex flex-col items-center gap-4 text-sm md:flex-row md:justify-between">
            <div className="flex items-center">
              <span className="mr-2">🎯</span>
            </div>
            <div>
              © {year} <Translate id="footer.copyright">OverBlock</Translate>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
